import React, { useEffect, useState } from "react";
import { CP, getProgress } from "../../helpers/helpers";

export function DarkMeter({
  startSec,
  endSec,
  imgSrc,
  imgClass,
  isDepleting = false,
  showTooltip = false,
  reverseTooltip = false,
  walletAddress,
  onClick,
  isTraining = false,
  trainingEndSec,
  availableBonks,
  passUntil,
  hibernating,
  isDarkMode,
}: {
  startSec?: number;
  endSec?: number;
  imgSrc: string;
  imgClass: React.HTMLAttributes<HTMLImageElement>["className"];
  isDepleting?: boolean;
  showTooltip?: boolean;
  reverseTooltip?: boolean;
  walletAddress: string;
  onClick?: () => void;
  isTraining?: boolean;
  trainingEndSec?: number;
  availableBonks?: number;
  passUntil?: number;
  hibernating?: boolean;
  isDarkMode: boolean;
}) {
  const [progress, setProgress] = useState(() => {
    if (passUntil !== undefined) {
      return getProgress(Math.floor(Date.now() / 1000), passUntil, true);
    } else if (availableBonks !== undefined) {
      return (availableBonks / 32) * 100;
    } else {
      return getProgress(startSec!, endSec!, isDepleting);
    }
  });

  const [hover, setHover] = useState(false);

  const [tooltipString, setTooltipString] = useState("");
  const isTouchDevice = "ontouchstart" in window;

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);

      if (passUntil !== undefined) {
        const timeRemaining = passUntil - now;

        if (timeRemaining > 0) {
          const maxDuration = 30 * 86400;
          const progress = timeRemaining <= maxDuration ? (timeRemaining / maxDuration) * 100 : 100;
          setProgress(progress);
        } else {
          setProgress(0);
        }
      } else if (availableBonks !== undefined) {
        setProgress((availableBonks / 32) * 100);
      } else {
        if (isTraining && trainingEndSec && now > trainingEndSec) {
          setProgress(100);
        } else if (!isTraining && trainingEndSec && now > trainingEndSec) {
          setProgress(0);
        } else {
          setProgress(getProgress(startSec!, endSec!, isDepleting));
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startSec, endSec, isDepleting, isTraining, trainingEndSec, availableBonks, passUntil]);

  const numBars = 16;
  const filledBars = Math.floor((progress / 100) * numBars);
  const isFull = filledBars === numBars;
  let currentProgress = progress;
  let dying = false;

  let progressBarClass = "bg-green";

  if (hibernating) {
    progressBarClass = "bg-red";
  } else if (availableBonks === 0) {
    progressBarClass = "bg-red";
    currentProgress = 100;
  } else if (isFull) {
    progressBarClass = "bg-darkgreen";
  } else if (isDepleting) {
    if (progress <= 10 && progress >= 0.01) {
      progressBarClass = "bg-red";
      dying = true;
    } else if (progress <= 25) {
      progressBarClass = "bg-red";
    } else {
      progressBarClass = "bg-green";
    }
  } else {
    progressBarClass = "bg-green";
  }

  return (
    <div
      className="flex w-full items-center space-x-2"
      onMouseEnter={() => !isTouchDevice && setHover(true)}
      onMouseLeave={() => !isTouchDevice && setHover(false)}
      onClick={onClick}
    >
      <div className="h-6 w-6">
        {CP.includes(walletAddress) && showTooltip && hover && (
          <div className="absolute ml-[-28px] mt-[-28px] w-32 justify-center rounded-md border-2 border-darkgreen bg-darkblue p-1 text-sm shadow-md">
            {tooltipString}
          </div>
        )}
        <img src={imgSrc} alt="" className={imgClass} />
      </div>

      <div
        className={`flex h-[50%] grow flex-row rounded-lg border-2  border-darkgreen bg-darkblue`}
      >
        <div className="flex h-full w-full items-center gap-[1px] px-[2px]">
          {Array.from({ length: 16 }).map((_, i) => {
            const threshold = ((i + 1) / numBars) * 100;
            const isActive = currentProgress >= threshold;
            return (
              <div
                key={i}
                className={`rounded-xs h-1 flex-1 ${
                  isActive ? `${progressBarClass}` : isDarkMode ? "bg-darkblue" : "bg-darkblue"
                }`}
              ></div>
            );
          })}
        </div>
      </div>

      {dying && <div className="mb-[0.1rem] text-sm font-semibold"> ! </div>}
    </div>
  );
}
